import {
  ImageLink,
  NonTextLink,
} from '@rsa-digital/evo-shared-components/commonTypes/links';
import { LanguageToggleProps } from '@rsa-digital/evo-shared-components/components/Header/DoubleHeader/LanguageToggle';
import {
  CardSection,
  LinkSection,
  PrimaryNav,
  PrimaryNavItem,
  TertiaryNavItemWithIcon,
} from '@rsa-digital/evo-shared-components/components/Header/types';
import { graphql, useStaticQuery } from 'gatsby';
import { MouseEventHandler } from 'react';
import {
  processImageAsset,
  processOptionalCta,
  unwrapSingleton,
} from 'helpers/csTypeProcessors';
import { nonFatalBuildError } from 'helpers/errorReporting';
import { trackMainNavigationClick } from 'helpers/eventTracking';
import { handleAndTrackLinkClick, handleLinkClick } from 'helpers/navigation';
import useLivePreviewSetup from 'helpers/useLivePreviewSetup';
import { CsAsset, CsCta, CsIcon } from 'types/contentStack';

type HeaderProps = {
  logoLink: ImageLink;
  phoneIconLink?: ImageLink; // defined only if both url and image are present in CS
  primaryNav: PrimaryNav;
  tertiaryNavItems: TertiaryNavItemWithIcon[];
  languageToggle?: LanguageToggleProps;
  mobileMenuOnOpen: MouseEventHandler;
  mobileMenuOnClose: MouseEventHandler;
};

type HeaderData = {
  allCsHeader: { nodes: CsHeader[] };
};

type CsHeader = {
  cslp__meta: unknown;
  locale: string;
  logo: {
    image: CsAsset;
    link_screen_reader_text: string;
    url: string;
  };
  phone_icon: {
    image: CsAsset;
    link_screen_reader_text: string;
    url: string;
  };
  primary_nav: {
    primary_nav_item: CsPrimaryNavItem;
  }[];
  tertiary_nav_items: CsTertiaryNavItem[];
  // This is an array because ContentStack does not allow optional group sections. So we set it to allow multiple but with a maximum of 1.
  language_toggle: [] | [LanguageToggleProps];
};

type CsPrimaryNavItem = {
  name: string;
  sections: CsPrimaryNavItemSection[];
};

type CsPrimaryNavItemSection = {
  links_section?: LinkSection;
  card_section?: CsCardSection;
};

type CsCardSection = {
  heading: string;
  text: string;
  cta: [CsCta] | null;
};

type CsTertiaryNavItem = {
  name: string;
  url: string;
  open_in_new_tab: boolean;
  custom_referrer_policy: string;
  icon: [CsIcon];
};

const headerQuery = graphql`
  query {
    allCsHeader {
      nodes {
        cslp__meta
        locale
        logo {
          image {
            ...Asset
          }
          link_screen_reader_text
          url
        }
        phone_icon {
          image {
            ...Asset
          }
          link_screen_reader_text
          url
        }
        primary_nav {
          primary_nav_item {
            name
            sections {
              links_section {
                heading
                links {
                  text
                  url
                }
              }
              card_section {
                heading
                text
                cta {
                  ...Cta
                }
              }
            }
          }
        }
        tertiary_nav_items {
          name
          url
          open_in_new_tab
          custom_referrer_policy
          icon {
            ...Icon
          }
        }
        language_toggle {
          text
          url
        }
      }
    }
  }
`;

const mapLogoLink = (csHeader: CsHeader): NonTextLink => ({
  url: csHeader.logo.url,
  screenReaderText: csHeader.logo.link_screen_reader_text,
  onClick: handleAndTrackLinkClick(
    csHeader.logo.url,
    'homeLogo',
    csHeader.logo.link_screen_reader_text
  ),
});

const mapPhoneIconLink = (csHeader: CsHeader): NonTextLink => ({
  url: csHeader.phone_icon.url,
  screenReaderText: csHeader.phone_icon.link_screen_reader_text,
  onClick: handleAndTrackLinkClick(
    csHeader.phone_icon.url,
    'phoneIconClick',
    csHeader.phone_icon.link_screen_reader_text
  ),
});

const mapSections = (
  sections: CsPrimaryNavItemSection[]
): (LinkSection | CardSection)[] =>
  sections.map((section) => {
    if (section.card_section) {
      return {
        heading: section.card_section.heading,
        text: section.card_section.text,
        cta: processOptionalCta(section.card_section.cta, 'HeadingCTA'),
      };
    }
    if (section.links_section) {
      return {
        heading: section.links_section.heading,
        links: section.links_section.links.map((link) => ({
          text: link.text,
          url: link.url,
          onClick: (event) => {
            trackMainNavigationClick(link.text, 'Click')();
            handleLinkClick(link.url)(event);
          },
        })),
      };
    }
    throw new Error('Header section must either be links section or card section');
  });

const mapPrimaryNav = (csHeader: CsHeader): PrimaryNavItem[] =>
  csHeader.primary_nav.map((navItem) => ({
    name: navItem.primary_nav_item.name,
    sections: mapSections(navItem.primary_nav_item.sections).slice(0, 4), // Primary nav can display a maximum of 4 sections
    onCollapse: trackMainNavigationClick(navItem.primary_nav_item.name, 'Collapse'),
    onExpand: trackMainNavigationClick(navItem.primary_nav_item.name, 'Expand'),
    ctas: [],
  }));

const mapTertiaryNavItems = (csHeader: CsHeader): TertiaryNavItemWithIcon[] =>
  csHeader.tertiary_nav_items.map((navItem) => {
    const iconCode = unwrapSingleton(navItem.icon)?.icon_code;
    /* istanbul ignore if */
    if (iconCode === undefined) {
      nonFatalBuildError(
        'Icon missing',
        `Icon expected for tertiary nav item "${navItem.name}"`
      );
    }

    return {
      name: navItem.name,
      url: navItem.url,
      openInNewTab: navItem.open_in_new_tab,
      customReferrerPolicy: navItem.custom_referrer_policy,
      icon: {
        name: iconCode ?? '',
        size: 'small',
      },
      onClick: (event) => {
        handleLinkClick(navItem.url)(event);
      },
    };
  });

const mapOptionalLanguageToggle = (
  csHeader: CsHeader
): LanguageToggleProps | undefined => {
  const languageToggle = unwrapSingleton(csHeader.language_toggle);
  return languageToggle
    ? {
        ...languageToggle,
        onClick: handleAndTrackLinkClick(
          languageToggle.url,
          'LanguageToggle',
          languageToggle.text
        ),
      }
    : undefined;
};

export const useHeaderData = (locale: string): HeaderProps => {
  const { allCsHeader } = useStaticQuery<HeaderData>(headerQuery);

  const csHeader = allCsHeader.nodes.find((header) => header.locale === locale);
  const headerData = useLivePreviewSetup(csHeader);
  if (!headerData) {
    throw new Error(`Header for locale: ${locale} is missing!`);
  }

  const logo = processImageAsset(headerData.logo.image);
  if (!logo) {
    throw new Error('Header logo missing!');
  }

  const phoneIconImage = processImageAsset(headerData.phone_icon?.image);

  // phoneIconLink only defined if both image and url are present in CS
  const phoneIconLink =
    phoneIconImage && headerData.phone_icon?.url
      ? {
          image: phoneIconImage,
          link: mapPhoneIconLink(headerData),
        }
      : undefined;

  return {
    logoLink: {
      image: logo,
      link: mapLogoLink(headerData),
    },
    phoneIconLink,
    primaryNav: {
      navItems: mapPrimaryNav(headerData),
    },
    tertiaryNavItems: mapTertiaryNavItems(headerData),
    languageToggle: mapOptionalLanguageToggle(headerData),
    mobileMenuOnOpen: trackMainNavigationClick('Mobile menu toggle', 'Open'),
    mobileMenuOnClose: trackMainNavigationClick('Mobile menu toggle', 'Close'),
  };
};
